/**
 * Pure merge logic for the map's claim-coloring state. Nothing here calls
 * get()/set() — all inputs are explicit arguments and outputs are plain
 * data, same convention as shardLoader.ts.
 *
 * Three sources feed `claimsByStructureId`, merged last-wins:
 *   1. `byArea` — the CF-derived `claimSummaries/{areaId}` baseline
 *      (`_areaClaimsByChunk` shape). Authoritative for every claim in the
 *      subscribed areas, but can lag several seconds behind an actual
 *      claim/unclaim write (Cloud Function trigger + possible cold start).
 *   2. `ownClaims` — this user's own claims from a direct
 *      `structureClaims` subscription (`where('claimedBy','==',uid)`).
 *      Not CF-derived, so it reflects this user's own claim state faster
 *      and more reliably than the area baseline — including claims made
 *      on another device.
 *   3. `optimistic` — a local claim/unclaim overlay the store writes
 *      synchronously the instant the user acts, before either subscription
 *      above has had a chance to fire. A `null` summary is an "unclaim"
 *      tombstone: it deletes the key from the merged result rather than
 *      merging in a value.
 */

import type { MapClaimSummary } from '../../types';

// How long an optimistic overlay entry stays in effect before being
// dropped outright, even if neither the area baseline nor the own-claims
// subscription has caught up. Bounds the worst case (a write that silently
// never propagates) so a stale entry can't haunt the map forever.
export const OPTIMISTIC_CLAIM_TTL_MS = 30_000;

export interface OptimisticClaimEntry {
  /** null = optimistic "unclaim" tombstone. */
  summary: MapClaimSummary | null;
  at: number;
}

/**
 * An optimistic entry is ignored (treated as if it weren't there) once:
 *   - it's older than `OPTIMISTIC_CLAIM_TTL_MS`, or
 *   - for a claim entry: `ownClaims` already has this structure — step 2
 *     below already produces the right answer, so the overlay is redundant.
 *
 * A tombstone deliberately retires on TTL ONLY. Retiring it the moment
 * `ownClaims` confirms the claim is gone would hand the structure back to
 * the area baseline, which can still carry the stale claim for several
 * CF-lagged seconds — the pin would flip back to "claimed" before settling.
 * The cost is the rare cross-device race (unclaim here, re-claim elsewhere
 * within 30s) staying hidden until the TTL lapses.
 */
function isRetiredOrExpired(
  entry: OptimisticClaimEntry,
  ownClaims: Record<string, MapClaimSummary>,
  structureId: string,
  now: number
): boolean {
  if (now - entry.at > OPTIMISTIC_CLAIM_TTL_MS) return true;
  if (entry.summary === null) return false;
  return Object.prototype.hasOwnProperty.call(ownClaims, structureId);
}

export function mergeClaimSummaries(
  byArea: Record<string, Record<string, MapClaimSummary>>,
  ownClaims: Record<string, MapClaimSummary>,
  optimistic: Record<string, OptimisticClaimEntry>,
  now: number
): Record<string, MapClaimSummary> {
  const merged: Record<string, MapClaimSummary> = {};

  // 1. Area baseline — every subscribed area's claimSummaries doc.
  for (const areaId in byArea) {
    const bucket = byArea[areaId];
    for (const structureId in bucket) merged[structureId] = bucket[structureId];
  }

  // 2. Own claims — authoritative override for this user's own claims.
  for (const structureId in ownClaims) {
    merged[structureId] = ownClaims[structureId];
  }

  // 3. Optimistic overlay — wins last, unless retired or expired.
  for (const structureId in optimistic) {
    const entry = optimistic[structureId];
    if (isRetiredOrExpired(entry, ownClaims, structureId, now)) continue;
    if (entry.summary === null) {
      delete merged[structureId];
    } else {
      merged[structureId] = entry.summary;
    }
  }

  return merged;
}

/**
 * Same retirement/TTL rule as `mergeClaimSummaries`, but returns the
 * surviving subset of `optimistic` itself so the store can garbage-collect
 * the overlay instead of letting it grow forever. Returns the original
 * object reference when nothing changed, so callers can skip a `set()`.
 */
export function pruneOptimisticClaims(
  ownClaims: Record<string, MapClaimSummary>,
  optimistic: Record<string, OptimisticClaimEntry>,
  now: number
): Record<string, OptimisticClaimEntry> {
  let changed = false;
  const next: Record<string, OptimisticClaimEntry> = {};
  for (const structureId in optimistic) {
    const entry = optimistic[structureId];
    if (isRetiredOrExpired(entry, ownClaims, structureId, now)) {
      changed = true;
      continue;
    }
    next[structureId] = entry;
  }
  return changed ? next : optimistic;
}
